// getPostMessageLcpPreloadMetaTags.js
'use strict';
import {
  POST_DETAIL_THUMBNAIL_SIZE,
  PREVIEW_PRESET,
} from '../RemoteConfigKeys.js';
import getMessageThumbnail from '../selector/getMessageThumbnail.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { breakpoint } from '../style/variables.js';
import { formatMimeType, MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';

const LCP_PRELOAD_FORMATS = [
  MediaAssetFormat.AVIF,
  MediaAssetFormat.WEBP,
  MediaAssetFormat.JPG,
];

const LCP_LAYOUT_TYPES = new Set(['post', 'flix']);

/**
 * Build <link rel="preload"> meta tag descriptors for a flix|post detail page's LCP image.
 * Emits one tag per (breakpoint × format); each browser picks the right format via
 * `type` and the right size via `media`.
 *
 * @param {object} params
 * @param {object} params.state - Redux state.
 * @param {string} params.messageId - Message id of the post.
 * @param {string} params.layoutType - Supported values: 'post', 'flix'.
 * @returns {Array<object>} Preload meta tag descriptors; [] when not flix|post or thumbnail missing.
 * @example
 * getPostMessageLcpPreloadMetaTags({ state, messageId: 'msg123', layoutType: 'flix' });
 * // => [{ tagName: 'link', rel: 'preload', type: 'image/avif', media: '...', ... }, ...]
 */
const getPostMessageLcpPreloadMetaTags = ({ state, messageId, layoutType }) => {
  if (!LCP_LAYOUT_TYPES.has(layoutType)) return [];

  const baseSize =
    getRemoteConfigData(state, POST_DETAIL_THUMBNAIL_SIZE) || 256;
  const isPreviewClear = getRemoteConfigData(state, PREVIEW_PRESET) === 'clear';

  const href = getMessageThumbnail(
    state,
    messageId,
    '',
    `${baseSize}x${baseSize}`,
    MediaAssetFormat.JPG,
    isPreviewClear ? '' : 'blurred'
  );
  if (!href) return [];
  const variants = [
    { size: baseSize, media: `(max-width: ${breakpoint.mobile - 1}px)` },
    { size: baseSize * 2, media: `(min-width: ${breakpoint.mobile}px)` },
  ];
  return variants.flatMap(({ size, media }) =>
    LCP_PRELOAD_FORMATS.map(format => {
      const { src, srcSet } = getPublicSrcSet({ href, size, format });
      return {
        tagName: 'link',
        rel: 'preload',
        as: 'image',
        href: src,
        imagesrcset: srcSet,
        imagesizes: `${size}px`,
        fetchpriority: 'high',
        type: formatMimeType[format],
        media,
      };
    })
  );
};
export default getPostMessageLcpPreloadMetaTags;
